import React, { useMemo, useState } from 'react';
import { bool, func } from 'prop-types';
import { injectIntl, intlShape, FormattedMessage } from '../../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import { Form, PrimaryButton, FieldTextInput, FieldSelect, SocialLoginButton, InlineTextButton } from '../../../components';
import {
  composeValidators,
  emailFormatValid,
  maxLength,
  minLength,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  required,
  requiredStringNoTrim,
} from '../../../util/validators';

import css from './SignupForm.module.css';

const SignupFormComponent = props => {
  const {
    intl,
    rootClassName,
    className,
    onFormChange,
    socialLoginButtons,
    ...rest
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const [selectedEmailSignup, setSelectedEmailSignup] = useState(false);

  const classes = useMemo(() => classNames(rootClassName || css.root, className), [
    className,
    rootClassName,
  ]);

  const messages = useMemo(
    () => ({
      conversionMessage: intl.formatMessage({ id: 'SignupForm.conversionMessage' }),
      userIntentLabel: intl.formatMessage({ id: 'SignupForm.userIntentLabel' }),
      userIntentPlaceholder: intl.formatMessage({ id: 'SignupForm.userIntentPlaceholder' }),
      userIntentCustomer: intl.formatMessage({ id: 'SignupForm.userIntentCustomer' }),
      userIntentProvider: intl.formatMessage({ id: 'SignupForm.userIntentProvider' }),
      userTypeLabel: intl.formatMessage({ id: 'SignupForm.userTypeLabel' }),
      userTypePlaceholder: intl.formatMessage({ id: 'General.accountTypePlaceholder' }),
      userTypeIndividual: intl.formatMessage({ id: 'SignupForm.userTypeIndividual' }),
      userTypeCompany: intl.formatMessage({ id: 'SignupForm.userTypeCompany' }),
      userTypeRequired: intl.formatMessage({ id: 'SignupForm.userTypeRequired' }),
      emailLabel: intl.formatMessage({ id: 'SignupForm.emailLabel' }),
      emailPlaceholder: intl.formatMessage({ id: 'SignupForm.emailPlaceholder' }),
      emailRequired: intl.formatMessage({ id: 'SignupForm.emailRequired' }),
      emailInvalid: intl.formatMessage({ id: 'SignupForm.emailInvalid' }),
      passwordLabel: intl.formatMessage({ id: 'SignupForm.passwordLabel' }),
      passwordPlaceholder: intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' }),
      passwordRequired: intl.formatMessage({ id: 'SignupForm.passwordRequired' }),
      passwordTooShort: intl.formatMessage(
        { id: 'SignupForm.passwordTooShort' },
        { minLength: PASSWORD_MIN_LENGTH }
      ),
      passwordTooLong: intl.formatMessage(
        { id: 'SignupForm.passwordTooLong' },
        { maxLength: PASSWORD_MAX_LENGTH }
      ),
      firstNameLabel: intl.formatMessage({ id: 'SignupForm.firstNameLabel' }),
      firstNamePlaceholder: intl.formatMessage({ id: 'SignupForm.firstNamePlaceholderShort' }),
      firstNameRequired: intl.formatMessage({ id: 'SignupForm.firstNameRequired' }),
      lastNameLabel: intl.formatMessage({ id: 'SignupForm.lastNameLabel' }),
      lastNamePlaceholder: intl.formatMessage({ id: 'SignupForm.lastNamePlaceholderShort' }),
      lastNameRequired: intl.formatMessage({ id: 'SignupForm.lastNameRequired' }),
      companyNameLabel: intl.formatMessage({ id: 'General.companyNameLabel' }),
      companyNamePlaceholder: intl.formatMessage({ id: 'General.companyNamePlaceholder' }),
      companyNameRequired: intl.formatMessage({ id: 'General.companyNameRequired' }),
      companyTypeLabel: intl.formatMessage({ id: 'General.companyTypeLabel' }),
      companyTypePlaceholder: intl.formatMessage({ id: 'General.companyTypePlaceholder' }),
      companyTypeRequired: intl.formatMessage({ id: 'General.companyTypeRequired' }),
      signUp: intl.formatMessage({ id: 'SignupForm.signUp' }),
    }),
    [intl]
  );

  return (
    <FinalForm
      {...rest}
      render={fieldRenderProps => {
        const { formId, handleSubmit, inProgress, invalid, values, form } = fieldRenderProps;

        const userIntent = values.userIntent;
        const isProvider = userIntent === 'provider';
        const isCustomer = userIntent === 'customer';
        const hasSelectedIntent = isProvider || isCustomer;
        const accountType = values.accountType;
        const isUserTypeCompany = accountType === 'certifiedBusiness';
        const submitDisabled = invalid || inProgress || !hasSelectedIntent;

        // Calculate progress percentage (3-step flow)
        let progressPercentage = 0;
        if (hasSelectedIntent) {
          progressPercentage = 33; // Step 1 complete
          if (selectedEmailSignup) {
            progressPercentage = 66; // Step 2 complete
            if (values.email && values.password && values.fname && values.lname) {
              progressPercentage = 100; // All fields complete
            }
          }
        }

        const resetFields = () => {
          form.change('fname', '');
          form.change('lname', '');
          form.change('cname', '');
          form.change('ctype', '');
          form.change('password', '');
        };

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {/* Track form changes to notify parent component */}
            {onFormChange && (
              <FormSpy
                subscription={{ values: true }}
                onChange={state => onFormChange(state.values)}
              />
            )}

            {/* Progress Bar */}
            <div className={css.progressBar}>
              <div className={css.progressFill} style={{ width: `${progressPercentage}%` }} />
            </div>

            <div className={css.fields}>
              {/* Step 1: User Intent Selection - Card-based buttons */}
              {!hasSelectedIntent && (
                <>
                  <h3 className={css.intentTitle}>{messages.userIntentLabel}</h3>
                  
                  <div className={css.intentCards}>
                    <button
                      type="button"
                      className={classNames(css.intentCard, css.intentCardCustomer)}
                      onClick={() => {
                        form.change('userIntent', 'customer');
                        form.change('isCompany', '');
                        resetFields();
                      }}
                    >
                      <h4 className={css.intentCardTitle}>{messages.userIntentCustomer}</h4>
                      <p className={css.intentCardDescription}>
                        <FormattedMessage id="SignupForm.customerDescription" />
                      </p>
                    </button>
                    
                    <button
                      type="button"
                      className={classNames(css.intentCard, css.intentCardProvider)}
                      onClick={() => {
                        form.change('userIntent', 'provider');
                        form.change('isCompany', '');
                        resetFields();
                      }}
                    >
                      <h4 className={css.intentCardTitle}>{messages.userIntentProvider}</h4>
                      <p className={css.intentCardDescription}>
                        <FormattedMessage id="SignupForm.providerDescription" />
                      </p>
                    </button>
                  </div>
                  
                  {/* Conversion Message as help text */}
                  <p className={css.conversionMessage}>{messages.conversionMessage}</p>
                </>
              )}
              {/* Step 2: Signup Method Selection - Show after intent selection */}
              {hasSelectedIntent && !selectedEmailSignup && (
                <>
                  {/* Back Button */}
                  <div className={css.backButton}>
                    <InlineTextButton
                      type="button"
                      onClick={() => {
                        form.change('userIntent', '');
                        form.change('isCompany', '');
                        resetFields();
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                      </svg>
                      <FormattedMessage id="SignupForm.back" />
                    </InlineTextButton>
                  </div>

                  {/* Social Login Buttons */}
                  {socialLoginButtons}

                  {/* Separator */}
                  <div className={css.signupMethodDivider}>
                    <span className={css.signupMethodDividerText}>
                      <FormattedMessage id="SignupForm.orSignupWithEmail" />
                    </span>
                  </div>

                  {/* Email Signup Button */}
                  <div className={css.emailSignupButtonWrapper}>
                    <SocialLoginButton
                      type="button"
                      onClick={() => setSelectedEmailSignup(true)}
                    >
                      <span className={css.buttonIcon}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" fill="none"/>
                          <polyline points="22,6 12,13 2,6" fill="none"/>
                        </svg>
                      </span>
                      <FormattedMessage id="SignupForm.signupWithEmail" />
                    </SocialLoginButton>
                  </div>
                </>
              )}

              {/* Step 3: Email Registration Form - Show after email method selected */}
              {hasSelectedIntent && selectedEmailSignup && (
                <>
                  {/* Back Button */}
                  <div className={css.backButton}>
                    <InlineTextButton
                      type="button"
                      onClick={() => setSelectedEmailSignup(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                      </svg>
                      <FormattedMessage id="SignupForm.back" />
                    </InlineTextButton>
                  </div>

                  {/* Account Type Selection for Providers */}
                  {isProvider && (
                    <FieldSelect
                      id="accountType"
                      name="accountType"
                      label={messages.userTypeLabel}
                      validate={composeValidators(
                        required(messages.userTypeRequired)
                      )}
                    >
                      <option disabled value="">
                        {messages.userTypePlaceholder}
                      </option>
                      <option value="certifiedProfessional">{messages.userTypeIndividual}</option>
                      <option value="certifiedBusiness">{messages.userTypeCompany}</option>
                    </FieldSelect>
                  )}

                  <FieldTextInput
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    autoComplete="email"
                    label={messages.emailLabel}
                    placeholder={messages.emailPlaceholder}
                    validate={composeValidators(
                      required(messages.emailRequired),
                      emailFormatValid(messages.emailInvalid)
                    )}
                  />

                  {/* Name fields - Always show for customers, optional for providers with company */}
                  {!isProvider || !isUserTypeCompany ? (
                    <div className={css.nameRow}>
                      <FieldTextInput
                        type="text"
                        id={formId ? `${formId}.fname` : 'fname'}
                        name="fname"
                        autoComplete="given-name"
                        label={messages.firstNameLabel}
                        placeholder={messages.firstNamePlaceholder}
                        validate={required(messages.firstNameRequired)}
                      />
                      <FieldTextInput
                        type="text"
                        id={formId ? `${formId}.lname` : 'lname'}
                        name="lname"
                        autoComplete="family-name"
                        label={messages.lastNameLabel}
                        placeholder={messages.lastNamePlaceholder}
                        validate={required(messages.lastNameRequired)}
                      />
                    </div>
                  ) : null}

                  {/* Company name field for providers with business account */}
                  {isProvider && isUserTypeCompany && (
                    <FieldTextInput
                      type="text"
                      id={formId ? `${formId}.cname` : 'cname'}
                      name="cname"
                      label={messages.companyNameLabel}
                      placeholder={messages.companyNamePlaceholder}
                      validate={required(messages.companyNameRequired)}
                    />
                  )}

                  <div className={css.passwordField}>
                <FieldTextInput
                  type={showPassword ? "text" : "password"}
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="new-password"
                  label={messages.passwordLabel}
                  placeholder={messages.passwordPlaceholder}
                  validate={composeValidators(
                    requiredStringNoTrim(messages.passwordRequired),
                    minLength(messages.passwordTooShort, PASSWORD_MIN_LENGTH),
                    maxLength(messages.passwordTooLong, PASSWORD_MAX_LENGTH)
                  )}
                />
                <button
                  type="button"
                  className={css.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex="-1"
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none"/>
                      <circle cx="12" cy="12" r="3" fill="none"/>
                      <line x1="3" y1="3" x2="21" y2="21"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none"/>
                      <circle cx="12" cy="12" r="3" fill="none"/>
                    </svg>
                  )}
                </button>
              </div>

              {/* Trust Badge & Value Prop - Only show in email form */}
              {selectedEmailSignup && (
                <div className={css.trustBadge}>
                  <div className={css.trustIcon}>🔒</div>
                  <div className={css.trustText}>
                    <FormattedMessage id="SignupForm.trustBadge" />
                  </div>
                </div>
              )}
              </>
            )}
            </div>

            {/* Submit button - Only show in email signup flow */}
            {selectedEmailSignup && (
              <div className={css.bottomWrapper}>
                <PrimaryButton
                  type="submit"
                  className={css.modalButton}
                  inProgress={inProgress}
                  disabled={submitDisabled}
                >
                  {messages.signUp}
                </PrimaryButton>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

SignupFormComponent.defaultProps = { 
  inProgress: false,
  onFormChange: null,
};

SignupFormComponent.propTypes = {
  inProgress: bool,
  onFormChange: func,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = injectIntl(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
